import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getData, postData } from '../middleware/apiMiddleware';
import s from './KeyLuckyBagPanel.module.css';

const TYPE_OPTIONS = ['complex-sentence', 'serial-story', 'literature-reading'];
const DIFFICULTY_OPTIONS = ['Pre-DSE', 'Essential', 'Intermediate+', 'Advanced'];
const NULL_FILTER_VALUE = '_NULL_';

export default function KeyLuckyBagPanel() {
	const navigate = useNavigate();
	const [luckyBags, setLuckyBags] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [editingCell, setEditingCell] = useState(null);
	const [editingName, setEditingName] = useState('');
	const [editingNameHk, setEditingNameHk] = useState('');

	// --- Filter States ---
	const [selectedType, setSelectedType] = useState('');
	const [selectedDifficulty, setSelectedDifficulty] = useState('');
	const [nameFilter, setNameFilter] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('');

	// --- Create Modal States ---
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [newItemName, setNewItemName] = useState('');
	const [isCreating, setIsCreating] = useState(false);

	const fetchList = useCallback(() => {
		getData(`item/listKeyLuckBag`, navigate)
		.then(res => {
			if (res && res.data) {
				setLuckyBags(res.data);
			} else {
				setLuckyBags([]);
			}
		})
		.catch(err => {
			console.error("Failed to fetch lucky bags:", err);
			setError("Failed to load lucky bags. Please try again.");
		})
		.finally(() => {
			if (isLoading) setIsLoading(false);
		});
	}, [navigate, isLoading]);

	useEffect(() => {
		fetchList();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filteredBags = useMemo(() => {
		const trimmedNameFilter = nameFilter.trim().toLowerCase();

		return luckyBags.filter(bag => {
			let typeMatch = true;
			if (selectedType === NULL_FILTER_VALUE) typeMatch = bag.type === null;
			else if (selectedType) typeMatch = bag.type === selectedType;

			let difficultyMatch = true;
			if (selectedDifficulty === NULL_FILTER_VALUE) difficultyMatch = bag.difficulty === null;
			else if (selectedDifficulty) difficultyMatch = bag.difficulty === selectedDifficulty;

			const nameMatch = trimmedNameFilter
				? (bag.name || '').toLowerCase().includes(trimmedNameFilter) ||
				  (bag.name_hk || '').toLowerCase().includes(trimmedNameFilter)
				: true;
			
			let statusMatch = true;
			if (selectedStatus) {
				statusMatch = bag.is_published === (selectedStatus === 'true');
			}
				
			return typeMatch && difficultyMatch && nameMatch && statusMatch;
		});
	}, [luckyBags, selectedType, selectedDifficulty, nameFilter, selectedStatus]);

	const handleSelectUpdate = async (itemId, field, newValue) => {
		const itemToUpdate = luckyBags.find(bag => bag.item_id === itemId);
		if (!itemToUpdate) return;
		setEditingCell(null);

		const valueToSend = newValue === "" ? null : newValue;
		const payload = {
			item_id: itemId,
			type: field === 'type' ? valueToSend : itemToUpdate.type,
			difficulty: field === 'difficulty' ? valueToSend : itemToUpdate.difficulty,
		};

		try {
			await postData("item/updateKeyLuckBag", payload, navigate);
			setLuckyBags(currentBags =>
				currentBags.map(bag => (bag.item_id === itemId ? { ...bag, [field]: valueToSend } : bag))
			);
		} catch (error) { console.error("Update failed:", error); alert("Update failed."); }
	};
	
	const handleNameUpdate = async (itemId, newName) => {
		const itemToUpdate = luckyBags.find(bag => bag.item_id === itemId);
		if (!itemToUpdate || itemToUpdate.name === newName || !newName.trim()) {
			setEditingCell(null);
			return;
		}
		setEditingCell(null);
		const payload = { itemId: String(itemId), name: newName.trim() };
		try {
			await postData("item/updateName", payload, navigate);
			setLuckyBags(currentBags =>
				currentBags.map(bag => (bag.item_id === itemId ? { ...bag, name: payload.name } : bag))
			);
		} catch (error) { console.error("Name update failed:", error); alert("Name update failed."); }
	};

	const handleNameHkUpdate = async (itemId, newNameHk) => {
		const itemToUpdate = luckyBags.find(bag => bag.item_id === itemId);
		const trimmedNameHk = newNameHk ? newNameHk.trim() : null;
		if (!itemToUpdate || itemToUpdate.name_hk === trimmedNameHk) {
			setEditingCell(null);
			return;
		}
		setEditingCell(null);
		const payload = { itemId: String(itemId), nameHk: trimmedNameHk };
		try {
			await postData("item/updateNameHk", payload, navigate);
			setLuckyBags(currentBags =>
				currentBags.map(bag => (bag.item_id === itemId ? { ...bag, name_hk: payload.nameHk } : bag))
			);
		} catch (error) { console.error("Name (HK) update failed:", error); alert("Name (HK) update failed."); }
	};

	const handlePublishToggle = async (itemId, currentStatus) => {
		const newStatus = !currentStatus;
		const payload = { itemId: String(itemId), isPublished: newStatus };

		setLuckyBags(currentBags =>
			currentBags.map(bag =>
				bag.item_id === itemId ? { ...bag, is_published: newStatus } : bag
			)
		);

		try {
			await postData("item/publish", payload, navigate);
		} catch (error) {
			console.error("Publish status update failed:", error);
			alert("Failed to update publish status.");
			setLuckyBags(currentBags =>
				currentBags.map(bag =>
					bag.item_id === itemId ? { ...bag, is_published: currentStatus } : bag
				)
			);
		}
	};

	const handleKeyDown = (e, handler, itemId, value) => {
		if (e.key === 'Enter') handler(itemId, value);
		else if (e.key === 'Escape') setEditingCell(null);
	};

	const handleCreateItem = async () => {
		if (!newItemName.trim()) {
			alert("Please enter a name for the new lucky bag.");
			return;
		}
		setIsCreating(true);
		const payload = { name: newItemName.trim(), itemType: 84 };
		try {
			await postData("item/createItem", payload, navigate);
			setIsCreateModalOpen(false);
			setNewItemName('');
			fetchList();
		} catch (error) {
			console.error("Failed to create item:", error);
			alert("Failed to create the item. Please try again.");
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<div className={s.panelContainer}>
			{isCreateModalOpen && (
				<div className={s.modalBackdrop}>
					<div className={s.modalContent}>
						<h3>Create New Lucky Bag</h3>
						<div className={s.modalBody}>
							<label htmlFor="new-item-name">Name:</label>
							<input id="new-item-name" type="text" className={s.inlineInput} value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Enter the name..." autoFocus/>
						</div>
						<div className={s.modalFooter}>
							<button className={s.modalButton} onClick={() => setIsCreateModalOpen(false)} disabled={isCreating}>Cancel</button>
							<button className={`${s.modalButton} ${s.confirmButton}`} onClick={handleCreateItem} disabled={isCreating || !newItemName.trim()}>{isCreating ? 'Creating...' : 'Confirm'}</button>
						</div>
					</div>
				</div>
			)}
			
			<div className={s.filterContainer}>
				<button className={s.createButton} onClick={() => setIsCreateModalOpen(true)}>Create Lucky Bag (84)</button>
				<div className={s.filterGroup}>
					<label htmlFor="name-filter">Filter by Name:</label>
					<input id="name-filter" type="text" placeholder="Enter Name or Name (HK)..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} className={s.filterInput}/>
				</div>
                <div className={s.filterGroup}>
					<label htmlFor="status-filter">Status:</label>
					<select id="status-filter" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
						<option value="">All</option>
						<option value="true">Published</option>
						<option value="false">Unpublished</option>
					</select>
				</div>
				<div className={s.filterGroup}>
					<label htmlFor="type-filter">Type:</label>
					<select id="type-filter" value={selectedType} onChange={e => setSelectedType(e.target.value)}>
						<option value="">All</option> <option value={NULL_FILTER_VALUE}>(NULL)</option> {TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
					</select>
				</div>
				<div className={s.filterGroup}>
					<label htmlFor="difficulty-filter">Difficulty:</label>
					<select id="difficulty-filter" value={selectedDifficulty} onChange={e => setSelectedDifficulty(e.target.value)}>
						<option value="">All</option> <option value={NULL_FILTER_VALUE}>(NULL)</option> {DIFFICULTY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
					</select>
				</div>
			</div>

			{isLoading && <p>Loading lucky bags...</p>}
			{error && <p className={s.errorMessage}>Error: {error}</p>}

			{!isLoading && !error && (
				<div className={s.listContainer}>
					<div className={s.headerRow}>
						<div className={`${s.column} ${s.colId}`}>ID</div>
						<div className={`${s.column} ${s.colPublished}`}>Published</div>
						<div className={`${s.column} ${s.colType}`}>Type</div>
						<div className={`${s.column} ${s.colDifficulty}`}>Difficulty</div>
						<div className={`${s.column} ${s.colCount}`}>Item Count</div>
						<div className={`${s.column} ${s.colName}`}>Name</div>
						<div className={`${s.column} ${s.colNameHk}`}>Name (HK)</div>
					</div>
					<div className={s.dataRowsContainer}>
						{filteredBags.length > 0 ? (
							filteredBags.map((bag) => (
							<div key={bag.item_id} className={s.dataRow}>
								<div className={`${s.column} ${s.colId}`}>
									<Link to={`/generalitem/${bag.item_id}`} target="_blank" rel="noopener noreferrer">{bag.item_id}</Link>
								</div>
								<div className={`${s.column} ${s.colPublished}`}>
									<label className={s.toggleSwitch}>
										<input
											type="checkbox"
											checked={bag.is_published}
											onChange={() => handlePublishToggle(bag.item_id, bag.is_published)}
										/>
										<span className={s.slider}></span>
									</label>
								</div>
								<div className={`${s.column} ${s.colType}`}>
									{editingCell?.itemId === bag.item_id && editingCell?.field === 'type' ? (
										<select className={s.inlineSelect} value={bag.type || ''} onChange={(e) => handleSelectUpdate(bag.item_id, 'type', e.target.value)} onBlur={() => setEditingCell(null)} autoFocus>
											<option value="">(Set to NULL)</option> {TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
										</select>
									) : (<span onClick={() => setEditingCell({ itemId: bag.item_id, field: 'type' })} className={s.editableCell}>{bag.type ? bag.type : <span className={s.nullValue}>NULL</span>}</span>)}
								</div>
								<div className={`${s.column} ${s.colDifficulty}`}>
									{editingCell?.itemId === bag.item_id && editingCell?.field === 'difficulty' ? (
										<select className={s.inlineSelect} value={bag.difficulty || ''} onChange={(e) => handleSelectUpdate(bag.item_id, 'difficulty', e.target.value)} onBlur={() => setEditingCell(null)} autoFocus>
											<option value="">(Set to NULL)</option> {DIFFICULTY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
										</select>
									) : (<span onClick={() => setEditingCell({ itemId: bag.item_id, field: 'difficulty' })} className={s.editableCell}>{bag.difficulty ? bag.difficulty : <span className={s.nullValue}>NULL</span>}</span>)}
								</div>
								{/* --- CHANGE IS HERE --- */}
								<div className={`${s.column} ${s.colCount} ${bag.related_item_count === 0 ? s.zeroCount : ''}`}>
									{bag.related_item_count === 0 ? 'NONE' : bag.related_item_count}
								</div>
								<div className={`${s.column} ${s.colName}`}>
									{editingCell?.itemId === bag.item_id && editingCell?.field === 'name' ? (
										<input type="text" className={s.inlineInput} value={editingName} onChange={(e) => setEditingName(e.target.value)} onBlur={() => handleNameUpdate(bag.item_id, editingName)} onKeyDown={(e) => handleKeyDown(e, handleNameUpdate, bag.item_id, editingName)} autoFocus/>
									) : (<span className={s.editableCell} onClick={() => { setEditingCell({ itemId: bag.item_id, field: 'name' }); setEditingName(bag.name); }}>{bag.name}</span>)}
								</div>
								<div className={`${s.column} ${s.colNameHk}`}>
									{editingCell?.itemId === bag.item_id && editingCell?.field === 'name_hk' ? (
										<input type="text" className={s.inlineInput} value={editingNameHk} onChange={(e) => setEditingNameHk(e.target.value)} onBlur={() => handleNameHkUpdate(bag.item_id, editingNameHk)} onKeyDown={(e) => handleKeyDown(e, handleNameHkUpdate, bag.item_id, editingNameHk)} autoFocus/>
									) : (<span className={s.editableCell} onClick={() => { setEditingCell({ itemId: bag.item_id, field: 'name_hk' }); setEditingNameHk(bag.name_hk || ''); }}>{bag.name_hk ? bag.name_hk : <span className={s.nullValue}>NULL</span>}</span>)}
								</div>
							</div>
						))
						) : (
							<p className={s.noDataMessage}>No matching items found.</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
}